/**
 * QAContentChecksPanel — Main dashboard panel showing qa_runs with expandable details.
 *
 * Displays a table of QA content check runs with color-coded pass/warn/fail statuses.
 * Click a row to expand and see individual check results.
 */

import { useState, useMemo } from 'react';
import { Spin, Empty } from 'antd';
import { RotateCcw, ShieldCheck, AlertTriangle, XCircle, Activity } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
} from '@/components/ui/table';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTenantStore } from '@/stores/useTenantStore';
import { useQARuns } from '@/hooks/useQAContentChecks';
import { QARunRow } from './QARunRow';
import type { QAContentCheckStatusFilter, QAContentCheckFilters } from '@/types/qaContentChecks';

const DEFAULT_FILTERS: QAContentCheckFilters = {
  status: 'all',
  dateFrom: '',
  dateTo: '',
};

export function QAContentChecksPanel() {
  const { currentTenant: tenantId } = useTenantStore();
  const [filters, setFilters] = useState<QAContentCheckFilters>(DEFAULT_FILTERS);

  const { data: runs, isLoading, error } = useQARuns(tenantId || '', filters);

  // Summary stats
  const stats = useMemo(() => {
    if (!runs) return { total: 0, pass: 0, warn: 0, fail: 0 };
    return {
      total: runs.length,
      pass: runs.filter((r) => r.overall_result === 'pass').length,
      warn: runs.filter((r) => r.overall_result === 'warn').length,
      fail: runs.filter((r) => r.overall_result === 'fail').length,
    };
  }, [runs]);

  const hasActiveFilters =
    filters.status !== 'all' || filters.dateFrom !== '' || filters.dateTo !== '';

  if (!tenantId) {
    return (
      <div className="p-6">
        <Empty description="Select a workspace to view QA content checks" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-teal" />
            <h1 className="text-2xl font-bold tracking-tight">Content QA Checks</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Automated SEO compliance checks run after publishing. Click a row to expand check details.
          </p>
        </div>

        {/* Summary stats cards */}
        {runs && runs.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard
              label="Total Runs"
              value={stats.total}
              icon={<Activity className="h-4 w-4 text-muted-foreground" />}
              className="border-border"
            />
            <StatCard
              label="Passed"
              value={stats.pass}
              icon={<ShieldCheck className="h-4 w-4 text-teal" />}
              className="border-teal/30"
              valueClass="text-teal-dark"
            />
            <StatCard
              label="Warnings"
              value={stats.warn}
              icon={<AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400" />}
              className="border-amber-200 dark:border-amber-800"
              valueClass="text-amber-600 dark:text-amber-400"
            />
            <StatCard
              label="Failed"
              value={stats.fail}
              icon={<XCircle className="h-4 w-4 text-destructive" />}
              className="border-destructive/30"
              valueClass="text-destructive"
            />
          </div>
        )}

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Select
            value={filters.status}
            onValueChange={(v) =>
              setFilters({ ...filters, status: v as QAContentCheckStatusFilter })
            }
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Result" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Results</SelectItem>
              <SelectItem value="pass">Pass</SelectItem>
              <SelectItem value="warn">Warning</SelectItem>
              <SelectItem value="fail">Failed</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground whitespace-nowrap">From</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-[150px] h-10"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground whitespace-nowrap">To</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-[150px] h-10"
            />
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset
            </Button>
          )}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Spin size="large" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="p-4 text-destructive bg-destructive/5 rounded-md">
            Failed to load QA content checks. Please try again.
          </div>
        )}

        {/* Empty state */}
        {runs && runs.length === 0 && (
          <Empty description="No QA check runs found. Checks run automatically after publishing blog posts." />
        )}

        {/* Runs table */}
        {runs && runs.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[calc(100vh-420px)]">
              <Table>
                <TableHeader className="sticky top-0 z-10">
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-8 px-2 bg-muted/50" />
                    <TableHead className="w-24 bg-muted/50">Status</TableHead>
                    <TableHead className="bg-muted/50">Keyword</TableHead>
                    <TableHead className="w-20 text-center bg-muted/50">Checks</TableHead>
                    <TableHead className="w-28 text-center bg-muted/50">Date</TableHead>
                    <TableHead className="w-20 text-center bg-muted/50">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run, index) => (
                    <QARunRow key={run.id} run={run} index={index} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Footer count */}
            <div className="flex items-center justify-between border-t px-4 py-3 bg-muted/30">
              <p className="text-sm text-muted-foreground tabular-nums">
                Showing <span className="font-medium text-foreground">{runs.length}</span> runs
              </p>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

/** Small stat card for the summary row */
function StatCard({
  label,
  value,
  icon,
  className,
  valueClass,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  className?: string;
  valueClass?: string;
}) {
  return (
    <div className={cn('border rounded-lg p-3 bg-background', className)}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
      </div>
      <p className={cn('text-2xl font-bold tabular-nums', valueClass)}>{value}</p>
    </div>
  );
}
